import React from 'react';
import {Platform, StyleSheet, View} from 'react-native';
import Localized from 'src/constants/AppStrings';
import Styles from 'src/components/Styles';
import AVText from 'src/components/elements/AVText';
import {scanAndPayTemplate} from 'src/components/screens/scanAndPay/templates/scanandPayThemeHelper';
import CreditCardInput from 'src/components/elements/funding/CreditCardInput';
import Settings from 'src/Settings';
import CustomTextInput from 'src/components/elements/CustomTextInput';
import CustomToggleSwitch from 'src/components/elements/CustomToggleSwitch';
import RoundedAppButton, {
  RoundedAppButtonType,
} from 'src/components/elements/RoundedAppButton';
import {getLayoutDescriber} from 'src/components/elements/layoutDescriptor/DescriptorType';

type AddCardProps = {
  cardNumber: string;
  expiryDate: string;
  cvv: string;
  zipCode: string;
  cardNumberError?: string;
  expDateError?: string;
  cvvError?: string;
  zipCodeError?: string;
  cardNumberAction: (text: string) => void;
  expMonthAction: (text: string) => void;
  cvvAction: (text: string) => void;
  zipAction: (text: string) => void;
  onCardNumberSubmitEditing?: () => void;
  isDefaultTokenDisabled?: boolean;
  defaultTokenStatus: boolean;
  switchAction: (value: boolean) => void;
  buttonAction: () => void;
  _isBtnActive?: boolean;
};

const AddCard: React.FC<AddCardProps> = ({
  cardNumber,
  expiryDate,
  cvv,
  zipCode,
  cardNumberError,
  expDateError,
  cvvError,
  zipCodeError,
  cardNumberAction,
  expMonthAction,
  cvvAction,
  zipAction,
  onCardNumberSubmitEditing,
  isDefaultTokenDisabled,
  defaultTokenStatus,
  switchAction,
  buttonAction,
  _isBtnActive,
}) => {
  return (
    <View style={styles.container}>
      <View accessible>
        <AVText
          style={styles.headerLabel}
          accessibilityLabel={
            Localized.Labels.add_credit_card_and_earn_reward_points
          }
          aria-label={Localized.Labels.add_credit_card_and_earn_reward_points}
          maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm2}
        >
          {Localized.Labels.add_credit_card_and_earn_reward_points}
        </AVText>
      </View>
      <>
        <CreditCardInput
          accessibilityLabel={Localized.Labels.card_number}
          onChangeText={(text) => cardNumberAction(text)}
          onSubmitEditing={onCardNumberSubmitEditing}
          label={Localized.Labels.card_number}
          value={cardNumber}
          textStyle={styles.creditCardInput}
          errorTextStyle={styles.errorText}
          hasErrorMsg={cardNumberError ? true : false}
          errorMsg={cardNumberError}
          outlineStyle={[styles.outline, cardNumberError && styles.errorBorder]}
          labelStyle={styles.cardNumberLabel}
          isRenderCustomText={true}
          testID="qa_fld_Card Number"
        />

        <View style={styles.row}>
          <View style={styles.inputRowLeft}>
            <CustomTextInput
              keyboardType="numeric"
              label={Localized.Labels.expires_mm_yy}
              value={expiryDate}
              accessible={true}
              accessibilityLabel={Localized.Labels.expires_mm_yy}
              maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm11}
              onChangeText={(text) => {
                expMonthAction(text);
              }}
              maxLength={5}
              textStyle={styles.creditCardInput}
              outlineStyle={[
                styles.outline,
                expDateError && styles.errorBorder,
              ]}
              errorTextStyle={styles.errorText}
              hasErrorMsg={expDateError ? true : false}
              error={!!expDateError}
              errorMsg={expDateError}
              labelStyle={styles.cardLabels}
              testID="qa_fld_Expires"
              theme={{
                colors: {
                  error: Styles.Colors.PayNew.error,
                },
              }}
            />
          </View>
          <View style={styles.inputRowRight}>
            <CustomTextInput
              value={cvv}
              label={Localized.Labels.security_code}
              onChangeText={(text) => cvvAction(text)}
              accessibilityLabel={Localized.Labels.security_code}
              keyboardType="numeric"
              accessible={true}
              accessibilityValue={{text: cvv}}
              maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm11}
              textStyle={styles.creditCardInput}
              outlineStyle={[styles.outline, cvvError && styles.errorBorder]}
              errorTextStyle={styles.errorText}
              hasErrorMsg={cvvError ? true : false}
              error={!!cvvError}
              errorMsg={cvvError}
              labelStyle={styles.cardLabels}
              testID=" qa_fld_SecurityCode"
              theme={{
                colors: {
                  error: Styles.Colors.PayNew.error,
                },
              }}
            />
          </View>
        </View>

        <CustomTextInput
          keyboardType="numeric"
          label={Localized.Labels.zip_postal_code}
          value={zipCode}
          accessible={true}
          accessibilityLabel={Localized.Labels.zip_postal_code}
          accessibilityValue={{text: zipCode}}
          onChangeText={(text) => zipAction(text)}
          maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm11}
          textStyle={styles.creditCardInput}
          outlineStyle={[styles.outline, zipCodeError && styles.errorBorder]}
          errorTextStyle={styles.errorText}
          hasErrorMsg={zipCodeError ? true : false}
          errorMsg={zipCodeError}
          labelStyle={styles.cardLabels}
          testID="qa_fld_ZipCode"
          theme={{
            colors: {
              error: Styles.Colors.PayNew.error,
            },
          }}
          error={!!zipCodeError}
        />
      </>

      <View style={styles.defaultStatusView}>
        <AVText
          accessible={true}
          maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm12}
          accessibilityLabel={Localized.Labels.set_as_default}
          accessibilityRole="text"
          aria-label={`${Localized.Labels.set_as_default} label, text`}
          style={styles.setDefaultLabel}
          testID="qa_setAsDefault-label-inactive"
          nativeID="qa_setAsDefault-label-inactive"
        >
          {Localized.Labels.set_as_default}
        </AVText>

        <CustomToggleSwitch
          accessibilityState={{
            checked: defaultTokenStatus,
            disabled: isDefaultTokenDisabled,
          }}
          accessibilityLabel={Localized.Labels.set_as_default}
          testID="qa_tgl_SetAsDefault"
          value={defaultTokenStatus}
          isDisabled={isDefaultTokenDisabled}
          onValueChange={(value) => switchAction(value)}
          containerStyle={styles.containerStyle}
        />
      </View>

      <RoundedAppButton
        accessibilityLabelValue={Localized.Buttons.save}
        accessibilityHintValue="Double tap to save your card information"
        aria-label={Localized.Buttons.save}
        buttonType={RoundedAppButtonType.Solid}
        onPress={buttonAction}
        titleText={Localized.Buttons.save}
        isDisabled={!_isBtnActive}
        buttonContainerStyle={
          Settings.isRefiveAnd365PayOnly()
            ? styles.btnContainerStyleT1
            : styles.btnContainerStyleT2
        }
        textStyle={[styles.btnTextStyle]}
        inActiveTextStyle={[styles.btnTextStyle]}
        buttonViewStyle={[
          Settings.isRefiveAnd365PayOnly()
            ? styles.btnViewStyleT1
            : styles.btnViewStyleT2,
          {
            backgroundColor: _isBtnActive
              ? scanAndPayTemplate.primaryColor
              : Styles.Colors.PayNew.darkHuesBase04,
          },
        ]}
        inActiveButtonViewStyle={
          Settings.isRefiveAnd365PayOnly()
            ? styles.btnViewStyleT1
            : styles.btnViewStyleT2
        }
        testID="qa_btn_Save"
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: Styles.Spacing.m3 + Styles.Spacing.m2,
    paddingTop: Styles.Spacing.m3,
  },

  errorText: {
    color: Styles.Colors.PayNew.error,
    marginTop: Styles.Spacing.m1,
    alignSelf: 'flex-start',
  },
  headerLabel: {
    fontSize: getLayoutDescriber().bodyLargeFontSize(),
    color: Styles.Colors.PayNew.black01,
    fontWeight: '700',
    fontFamily: scanAndPayTemplate.textFontFamily,
    alignItems: 'center',
  },
  creditCardInput: {
    fontSize: getLayoutDescriber().bodySmallFontSize(),
    fontFamily: scanAndPayTemplate.textFontFamily,
    color: Styles.Colors.PayNew.darkHuesBase04,
    fontWeight: '400',
  },
  cardNumberLabel: {
    fontSize: getLayoutDescriber().bodySmallFontSize(),
    fontFamily: scanAndPayTemplate.textFontFamily,
    fontWeight: '400',
    color: Styles.Colors.PayNew.darkHuesBase04,
  },
  outline: {
    borderRadius: Styles.Spacing.m2,
    borderWidth: 1,
    borderColor: Styles.Colors.PayNew.neutralHuesBase09,
  },
  inputRowLeft: {
    paddingRight: Styles.Spacing.m2,
    flex: 0.5,
  },
  row: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  inputRowRight: {
    flex: 0.5,
    paddingLeft: Styles.Spacing.m1,
  },
  errorBorder: {borderColor: Styles.Colors.PayNew.error},
  cardLabels: {
    fontSize: getLayoutDescriber().bodyFontSize(),
    fontFamily: scanAndPayTemplate.textFontFamily,
    fontWeight: '400',
    color: Styles.Colors.PayNew.darkHuesBase04,
  },
  defaultStatusView: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginTop: Styles.Spacing.m3 + Styles.Spacing.m1,
    ...(Platform.OS === 'web' && {
      paddingRight: Styles.Spacing.m4 + Styles.Spacing.m3,
    }),
  },
  setDefaultLabel: {
    fontSize: getLayoutDescriber().bodyFontSize(),
    marginRight: Styles.Spacing.m2,
    color: Styles.Colors.PayNew.black01,
    fontWeight: '400',
    fontFamily: scanAndPayTemplate.textFontFamily,
  },
  containerStyle: {justifyContent: 'flex-end', flex: 0, padding: 0},
  btnContainerStyleT1: {
    alignSelf: 'flex-end',
    marginTop: Styles.Spacing.m3 + Styles.Spacing.m2,
    marginBottom: Styles.Spacing.m3,
  },
  btnContainerStyleT2: {
    width: Platform.OS === 'web' ? '100%' : Styles.ScreenWidth,
    alignSelf: 'center',
    justifyContent: 'center',
    paddingHorizontal: Styles.Spacing.m3 + Styles.Spacing.m2,
    marginBottom: Styles.Spacing.m3 + Styles.Spacing.m2,
    marginTop: Styles.Spacing.m3 + Styles.Spacing.m2,
  },
  btnViewStyleT2: {
    width: '100%',
    backgroundColor: Styles.Colors.PayNew.darkHuesBase04,
    height: Styles.Heights.touchTargetHeight1 - 2,
  },
  btnViewStyleT1: {
    backgroundColor: Styles.Colors.PayNew.darkHuesBase04,
    height: Styles.Heights.touchTargetHeight1 - 2,
    paddingHorizontal: Styles.Spacing.m3,
  },
  btnTextStyle: {
    fontSize: getLayoutDescriber().bodyFontSize(),
    fontWeight: '700',
    color: Styles.white,
    paddingHorizontal: Styles.Spacing.m3,
    fontFamily: scanAndPayTemplate.textFontFamily,
  },
});

export default AddCard;
